import React, { useEffect, useMemo } from "react";

import { orderBy } from "lodash";

import { HookResult } from "@fartherfinance/frontend/api/Types";

import AssetAllocationPieChart, {
  AssetClassification,
} from "@src/multiCustodian/components/Portfolios/AssetAllocationPieChart/AssetAllocationPieChart";
import PortfolioMetricsTable from "@src/multiCustodian/components/Portfolios/PortfolioMetricsTable/PortfolioMetricsTable";
import useRequestAuth from "@src/multiCustodian/hooks/useRequestAuth";
import Text from "@src/sharedComponents/Typography/Text";

import { SubSection } from "./TransitionProposalDetailsSections";
import {
  InvalidPositions,
  isVirtualPortfolioPlan,
  TransitionProposalDetailsProps,
} from "./types";
import useGetTransitionProposalData from "./useGetTransitionProposalData";
import useGetVirtualPortfolioPlanData from "./useGetVirtualPortfolioPlanData";

import styles from "./TransitionProposalDetails.module.css";

const InvalidPositionsNotice: React.FC<{
  invalidPositions: InvalidPositions;
}> = ({ invalidPositions }) => {
  if (invalidPositions.length === 0) {
    return null;
  }
  return (
    <Text
      variant="bodySmall"
      color="textSubtle"
      className={styles.invalidPositionsNotice}
    >
      The following positions could not be identified and were excluded from the
      statistical analysis:{" "}
      {orderBy(invalidPositions, (v) => v, ["asc"]).join(", ")}.
    </Text>
  );
};

const TransitionProposalDetails: React.FC<TransitionProposalDetailsProps> = (
  props
) => {
  const auth = useRequestAuth();
  const getTransitionProposalData = useGetTransitionProposalData(auth);
  const getPortfolioPlanData = useGetVirtualPortfolioPlanData(auth);

  const {
    classifications,
    hasErrorClassifications,
    hasErrorPortfolioStats,
    isLoadingClassifications,
    isLoadingPortfolioStats,
    portfolioStats,
    invalidPositions,
  } = isVirtualPortfolioPlan(props)
    ? getPortfolioPlanData(props.portfolioPlanId, props.entries)
    : getTransitionProposalData(props.transitionProposalId);

  // Report errors to parent component
  useEffect(() => {
    if (props.onError) {
      props.onError(hasErrorClassifications || hasErrorPortfolioStats);
    }
  }, [hasErrorClassifications, hasErrorPortfolioStats, props]);

  const chartData: HookResult<AssetClassification[]> = useMemo(() => {
    if (isLoadingClassifications) {
      return {
        isLoading: true as const,
        hasError: false as const,
        data: undefined,
      };
    } else if (hasErrorClassifications) {
      return {
        isLoading: false as const,
        hasError: true as const,
        data: undefined,
        error: new Error("Unknown"),
      };
    }
    return {
      isLoading: false as const,
      hasError: false as const,
      data: classifications,
    };
  }, [classifications, hasErrorClassifications, isLoadingClassifications]);

  return (
    <>
      <SubSection title="Details">
        <AssetAllocationPieChart data={chartData} />
      </SubSection>

      <SubSection title="Stats">
        <PortfolioMetricsTable
          isLoading={isLoadingPortfolioStats}
          hasError={hasErrorPortfolioStats}
          data={portfolioStats}
          metricVisibilitySettings={props.metricVisibilitySettings}
          onToggleMetricVisibility={props.onToggleMetricVisibility}
          disableMetricVisibilityToggle={props.disableMetricVisibilityToggle}
        />
      </SubSection>

      <SubSection>
        <InvalidPositionsNotice invalidPositions={invalidPositions} />
      </SubSection>
    </>
  );
};

export default TransitionProposalDetails;
